import h from 'hyperscript';
import { ServerPostMessageApi } from 'cadenza/utils/post-message/server-post-message-api';
import { logger } from 'cadenza/utils/logging';
import 'ui/progress-spinner/progress-spinner';

import { View } from './view';

import './iframe-view.css';

const NAME = 'd-iframe-view';
const INVISIBLE_CLASS = 'invisible';

// Base class for views which contain an iframe
export class IFrameView extends View {

  #api;
  #progressSpinner;
  #iframe;

  constructor ({ postMessageApi = new ServerPostMessageApi() } = {}) {
    super();
    this.#api = postMessageApi;
    this.#progressSpinner = h('d-progress-spinner.position-centered', { hidden: true });
    this.#iframe = h(`iframe.${NAME}--iframe.${INVISIBLE_CLASS}`); // Do not use display none, otherwise the datatables will not init properly
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME);
    this.append(this.#progressSpinner, this.#iframe);
  }

  /**
   * The iframe this view uses to show its external content.
   *
   * @protected
   * @return {HTMLIFrameElement} The iframe this view uses to show its external content
   */
  get iframe () {
    return this.#iframe;
  }

  /**
   * @protected
   * @return {PostMessageApi} The post-message api for communicating with the iframe
   */
  get api () {
    return this.#api;
  }

  /** Reload iframe without asking content */
  reload () {
    // eslint-disable-next-line no-self-assign
    this.src = this.src;
  }

  /**
   * Initializes the iframe view by calling the src setter, with this initializing the loading of the iframe
   *
   * @protected
   * @param {string} url - the url to load the iframe from
   */
  initializeIframe (url) {
    this.#iframe.onload = () => this.onFrameLoad();
    this.#iframe.onerror = error => {
      this.#progressSpinner.hidden = true;
      this.#iframe.classList.remove(INVISIBLE_CLASS);
      logger.error(error);
    };
    if (url) {
      this.#progressSpinner.hidden = false;
      this.src = url; // this is necessary because src attribute is not changed on href change without reload
    }
  }

  /**
   * Sets the iframe src to the passed param
   *
   * @protected
   * @param {string} iframeSrc - the src for the iframe
   */
  set src (iframeSrc) {
    this.#iframe.src = iframeSrc;
  }

  get src () {
    return this.#iframe.src;
  }

  /**
   * Lifecycle method called when the iframe is loaded
   *
   * @protected
   */
  onFrameLoad () {
    if (!this.src) {
      return;
    }

    this.#api.connect(this.#iframe.contentWindow)
      .catch((error) => {
        this.api.endpoint = undefined; // To not collect silently and endlessly messages. Fail instead.
        logger.error('Failed to initialize postmessage API to iframe', error);
      });
    this.#progressSpinner.hidden = true;
    this.#iframe.classList.remove(INVISIBLE_CLASS);
  }

}
